import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { formatDate, parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import { MdOutlineEdit, MdBlock, MdMerge } from 'react-icons/md';
import { FiEye } from 'react-icons/fi';
import { FaPlus } from 'react-icons/fa6';
import { Campaign } from '~/types/campaigns';
import { getCampaignStatusBadgeClass } from '~/utils/campaignBadgeStyles';
import CampaignActionDropdown from '~/components/CampaignRequestDetails/CampaignActionDropdown';
import AddNonClientCampaignDrawer from '~/components/Campaigns/AddNonClientCampaignDrawer';
import {
    fetchCampaigns,
    fetchCampaignSearchFilters,
} from '~/services/campaign';
import { useQueries } from '@tanstack/react-query';
import { formatCurrency, ReactSelectInput } from '~/common/form/Inputs';
import { Pagination } from '~/components/Pagination';

const columnHelper = createColumnHelper<Campaign>();

const columns = [
    columnHelper.accessor('full_name', {
        header: () => 'Client',
        cell: (props) => {
            const patient_info = props.row.original.patient_info;
            let cityState = '';
            if (patient_info?.person?.city) {
                cityState = `${patient_info?.person?.city}, ${patient_info?.person?.state}`;
            }

            return (
                <>
                    <Link
                        className='font-bold text-primary'
                        to={`/campaigns/${props.row.original.campaign_id}`}
                    >
                        {props.renderValue()}
                    </Link>
                    <div className={'text-[12px]/[16px] text-[#949599]'}>
                        {cityState}
                    </div>
                </>
            );
        },
    }),
    columnHelper.accessor('campaign_status', {
        header: () => 'Status',
        cell: (props) => (
            <div
                className={`inline-flex rounded-full px-3 py-1 text-[12px]/[14.4px] hover:bg-opacity-90 ${getCampaignStatusBadgeClass(
                    props.getValue()
                )}`}
            >
                {props.renderValue()}
            </div>
        ),
    }),
    columnHelper.accessor('campaign_type.name', {
        header: () => 'Campaign Type',
        cell: (props) => {
            const medicalReason =
                props.row.original?.patient_info?.primary_medical_issue?.name;
            return (
                <>
                    {props.renderValue()}
                    <div className={'text-[12px]/[16px] text-[#949599]'}>
                        {medicalReason}
                    </div>
                </>
            );
        },
    }),
    columnHelper.accessor('client_services_coordinator', {
        id: 'csc',
        header: () => 'CSC',
        cell: (props) => props.renderValue(),
    }),
    columnHelper.accessor(
        (props) =>
            props.update_date
                ? formatDate(parseISO(props.update_date), 'MM/dd/yy')
                : '',
        {
            id: 'last_activity',
            header: () => 'Last Activity',
            cell: (props) => props.renderValue(),
        }
    ),
    columnHelper.accessor('entire_funds_raised', {
        id: 'entire_funds_raised',
        header: () => 'Donations',
        cell: (props) => {
            const val = props.getValue();
            if (!val) return <span></span>;
            const num = parseFloat(val);
            return formatCurrency(num);
        },
    }),
    columnHelper.accessor('unique_donor_count', {
        id: 'unique_donors',
        header: () => 'Unique Donors',
        cell: (props) => props.renderValue(),
    }),
];

enum Sort {
    ASC = 'asc',
    DESC = 'desc',
}

type FilterOption = {
    label: string;
    value: string;
};

type Filters = {
    status: FilterOption[];
    campaign_type_id: FilterOption[];
    region: FilterOption[];
    verifiedStatus: FilterOption[];
};

const initialFilters = {
    status: [],
    region: [],
    verifiedStatus: [],
    campaign_type_id: [],
};

type DateRange = {
    start_date: string;
    end_date: string;
};

const initialDateRange = {
    start_date: '',
    end_date: '',
};

const NonClientCampaigns: React.FC = () => {
    const [pageSize] = useState(20);
    const [pages, setPages] = useState<number>(1);
    const [page, setPage] = useState<number>(1);
    const [sort, setSort] = useState<Sort | null>(null);
    const [filters, setFilters] = useState<Filters | null>(null);
    const [selectedFilters, setSelectedFilters] =
        useState<Filters>(initialFilters);
    const [sortBy, setSortBy] = useState<string | null>(null);
    const [dateRange, setDateRange] = useState<DateRange>(initialDateRange);
    const [addOpen, setAddOpen] = useState(false);

    const results = useQueries({
        queries: [
            {
                queryKey: [
                    'campaign',
                    { pageSize, sort, sortBy, page },
                    selectedFilters,
                    dateRange,
                ],
                queryFn: () =>
                    fetchCampaigns(
                        { pageSize, sort, sortBy, non_client: true },
                        selectedFilters,
                        dateRange,
                        page
                    ),
                staleTime: Infinity,
            },
            {
                queryKey: ['campaign_search_filters'],
                queryFn: () => fetchCampaignSearchFilters(),
                staleTime: Infinity,
            },
        ],
    });
    const campaignResults = results[0]?.data;

    useEffect(() => {
        const total = campaignResults?.count;
        if (total) {
            setPages(Math.ceil(total / pageSize));
        }
    }, [campaignResults, pageSize]);

    useEffect(() => {
        const filterData = results[1]?.data;
        const verifiedOptions = [
            { label: 'Verified', value: 'verified' },
            { label: 'Unverified', value: 'unverified' },
        ];
        if (filterData && !filters) {
            const newFilters = {
                status: filterData.campaign_statuses.map((status) => ({
                    label: status.name,
                    value: status.code,
                })),
                campaign_type_id: filterData.campaign_types.map((reason) => ({
                    label: reason.name,
                    value: reason.id,
                })),
                region: filterData.campaign_regions.map((region) => ({
                    label: region.name,
                    value: region.code,
                })),
                verifiedStatus: verifiedOptions,
            };
            setFilters(newFilters);
        }
    }, [results]);

    const table = useReactTable({
        data: campaignResults?.results,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    const isLoading = results.some((result) => result.isLoading);
    const error = results.find((result) => result.error);

    if (error) return <p>Error: {error.message}</p>;

    const handleSort = (columnId: string) => {
        if (sortBy === columnId) {
            if (sort === Sort.ASC) {
                setSort(Sort.DESC);
            } else {
                setSort(Sort.ASC);
            }
        } else {
            setSortBy(columnId);
            setSort(Sort.ASC);
        }
    };

    const handleFilter = (filterType: string, value: string) => {
        setSelectedFilters({
            ...selectedFilters,
            [filterType]: value,
        });
    };

    return (
        <>
            <Breadcrumb pageName='Non-Client Campaigns' />

            <AddNonClientCampaignDrawer isOpen={addOpen} setIsOpen={setAddOpen} />

            <section className='data-table-common rounded-sm border border-stroke bg-white py-4 shadow-default dark:border-strokedark dark:bg-boxdark'>
                <div className='flex flex-wrap items-center justify-start gap-4 overflow-x-auto px-8 pb-4'>
                    {!filters?.status ? (
                        <div className='w-[120px] animate-pulse'>
                            <div className='h-4 rounded-full bg-gray-4 dark:bg-gray-5'></div>
                        </div>
                    ) : (
                        <div className='min-w-[120px] max-w-[120px]'>
                            <ReactSelectInput
                                options={filters?.status || []}
                                value={selectedFilters.status}
                                onChange={(value) =>
                                    handleFilter('status', value)
                                }
                                placeholder='Status'
                            />
                        </div>
                    )}
                    {!filters?.campaign_type_id ? (
                        <div className='w-[180px] animate-pulse'>
                            <div className='h-4 rounded-full bg-gray-4 dark:bg-gray-5'></div>
                        </div>
                    ) : (
                        <div className='min-w-[180px] max-w-[180px]'>
                            <ReactSelectInput
                                options={filters?.campaign_type_id || []}
                                value={selectedFilters.campaign_type_id}
                                onChange={(value) =>
                                    handleFilter('campaign_type_id', value)
                                }
                                placeholder='Campaign Type'
                            />
                        </div>
                    )}
                    {!filters?.region ? (
                        <div className='w-[175px] animate-pulse'>
                            <div className='h-4 rounded-full bg-gray-4 dark:bg-gray-5'></div>
                        </div>
                    ) : (
                        <div className='min-w-[175px] max-w-[175px]'>
                            <ReactSelectInput
                                options={filters?.region || []}
                                value={selectedFilters.region}
                                onChange={(value) =>
                                    handleFilter('region', value)
                                }
                                placeholder='Region'
                            />
                        </div>
                    )}
                    {!filters?.verifiedStatus ? (
                        <div className='w-[175px] animate-pulse'>
                            <div className='h-4 rounded-full bg-gray-4 dark:bg-gray-5'></div>
                        </div>
                    ) : (
                        <div className='min-w-[175px] max-w-[175px]'>
                            <ReactSelectInput
                                options={filters?.verifiedStatus || []}
                                value={selectedFilters.verifiedStatus}
                                onChange={(value) =>
                                    handleFilter('verifiedStatus', value)
                                }
                                placeholder='Verified Status'
                            />
                        </div>
                    )}
                    <input
                        type='date'
                        name='start_date'
                        className='rounded border border-[#E2E8F0] p-[6px]'
                        value={dateRange.start_date}
                        onChange={(event) =>
                            setDateRange({
                                ...dateRange,
                                start_date: event.target.value,
                            })
                        }
                    />
                    to
                    <input
                        type='date'
                        name='end_date'
                        className='rounded border border-[#E2E8F0] p-[6px]'
                        value={dateRange.end_date}
                        onChange={(event) =>
                            setDateRange({
                                ...dateRange,
                                end_date: event.target.value,
                            })
                        }
                    />
                    <button
                        type='button'
                        className='inline-flex shrink-0 items-center justify-center gap-2.5 rounded-full border-[2px] border-warning bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8]'
                        onClick={() => setAddOpen(true)}
                    >
                        <FaPlus />
                        Add Campaign
                    </button>
                </div>

                <div
                    className='flex items-center justify-between border-t border-stroke px-8 py-1'
                    style={{ fontSize: '12px' }}
                >
                    <p>Showing {Math.min(pageSize, (campaignResults?.count || 0) - (page - 1) * pageSize)} of {campaignResults?.count || 0} campaigns</p>

                    <div className='flex items-center gap-5'>
                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <FiEye className='text-primary' size={18} />
                            View
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdOutlineEdit className='text-primary' size={18} />
                            Edit
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdMerge className='text-primary' size={18} />
                            Merge
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdBlock className='text-red' size={18} />
                            Close
                        </div>
                    </div>
                </div>

                <div className='overflow-x-auto'>
                    <table className='datatable-table datatable-one w-full table-auto border-collapse break-words px-4 md:px-8 2xl:table-fixed'>
                        <thead className='border-separate px-4'>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr
                                    className='border-t border-stroke bg-whiten'
                                    style={{ fontSize: '14px' }}
                                    key={headerGroup.id}
                                >
                                    {headerGroup.headers.map((header) => {
                                        const useChevron =
                                            header.column.id === sortBy;
                                        let chevron = null;
                                        if (useChevron) {
                                            if (sort === Sort.ASC) {
                                                chevron = (
                                                    <svg
                                                        xmlns='http://www.w3.org/2000/svg'
                                                        viewBox='0 0 20 20'
                                                        fill='currentColor'
                                                        className='h-5 w-5'
                                                    >
                                                        <path
                                                            fillRule='evenodd'
                                                            d='M9.47 6.47a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 1 1-1.06 1.06L10 8.06l-3.72 3.72a.75.75 0 0 1-1.06-1.06l4.25-4.25Z'
                                                            clipRule='evenodd'
                                                        />
                                                    </svg>
                                                );
                                            } else if (sort === Sort.DESC) {
                                                chevron = (
                                                    <svg
                                                        xmlns='http://www.w3.org/2000/svg'
                                                        viewBox='0 0 20 20'
                                                        fill='currentColor'
                                                        className='h-5 w-5'
                                                    >
                                                        <path
                                                            fillRule='evenodd'
                                                            d='M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z'
                                                            clipRule='evenodd'
                                                        />
                                                    </svg>
                                                );
                                            }
                                        }

                                        return (
                                            <th
                                                style={{
                                                    paddingTop: '16px',
                                                    paddingBottom: '16px',
                                                }}
                                                key={header.id}
                                            >
                                                <div className='flex items-center'>
                                                    <span
                                                        onClick={() =>
                                                            handleSort(
                                                                header.column.id
                                                            )
                                                        }
                                                        className='cursor-pointer'
                                                    >
                                                        {header.isPlaceholder
                                                            ? null
                                                            : flexRender(
                                                                  header.column
                                                                      .columnDef
                                                                      .header,
                                                                  header.getContext()
                                                              )}
                                                    </span>
                                                    <div className='ml-2 inline-flex flex-col space-y-[2px]'>
                                                        <span className='inline-block'>
                                                            {chevron}
                                                        </span>
                                                    </div>
                                                </div>
                                            </th>
                                        );
                                    })}
                                    <th
                                        style={{
                                            width: '70px',
                                            paddingTop: '16px',
                                            paddingBottom: '16px',
                                        }}
                                    ></th>
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {isLoading
                                ? Array.from({ length: 10 }).map((_, index) => (
                                      <tr key={index}>
                                          {columns.map((_column, colIndex) => (
                                              <td
                                                  key={colIndex}
                                                  className='border-b border-stroke px-4 py-2 dark:border-strokedark'
                                              >
                                                  <div className='max-w-sm animate-pulse'>
                                                      <div className='h-2 max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                                  </div>
                                              </td>
                                          ))}
                                          <td className='border-b border-stroke px-4 py-2 dark:border-strokedark'>
                                              <div className='float-right w-full max-w-[10px] animate-pulse'>
                                                  <div className='h-[10px] max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                              </div>
                                          </td>
                                      </tr>
                                  ))
                                : table.getRowModel().rows.map((row) => (
                                      <tr
                                          className='border-t border-stroke'
                                          key={row.id}
                                      >
                                          {row.getVisibleCells().map((cell) => (
                                              <td
                                                  style={{
                                                      paddingTop: '16px',
                                                      paddingBottom: '16px',
                                                  }}
                                                  key={cell.id}
                                              >
                                                  {flexRender(
                                                      cell.column.columnDef
                                                          .cell,
                                                      cell.getContext()
                                                  )}
                                              </td>
                                          ))}
                                          <td
                                              className='text-right'
                                              style={{
                                                  paddingTop: '16px',
                                                  paddingBottom: '16px',
                                              }}
                                          >
                                              <CampaignActionDropdown
                                                  campaignId={
                                                      row?.original?.campaign_id
                                                  }
                                              />
                                          </td>
                                      </tr>
                                  ))}
                        </tbody>
                    </table>
                </div>

                <Pagination page={page} setPage={setPage} pages={pages} totalCount={campaignResults?.count} />
            </section>
        </>
    );
};

export default NonClientCampaigns;
